/**
 * Portal API client.
 *
 * ── Where the session token lives ─────────────────────────────────────────
 *
 * localStorage, which every tab on this origin shares.
 *
 * It was sessionStorage, which is per TAB, on the reasoning that the portal
 * can deploy code into customer infrastructure and so "a token surviving a
 * closed tab is a longer window than this deserves". That reasoning did not
 * survive contact with the rest of the system: sign-in also sets an
 * `sdportal_session` cookie with `Max-Age=43200`, and the server accepts it.
 * Twelve hours, persisting across a closed tab and a closed browser. The
 * window was already open; sessionStorage only hid it, at the cost of making
 * every new tab ask for a password that was not needed.
 *
 * So the two now agree. Both last twelve hours and both end at sign-out,
 * which clears the cookie as well as this — see signOut below.
 *
 * Read falls back to sessionStorage once, for whoever is mid-session when
 * this ships: their tab keeps working and is promoted rather than bounced to
 * a sign-in screen by an upgrade.
 */

const KEY = 'sdportal.session';

export const getToken = () => {
  const t = localStorage.getItem(KEY);
  if (t) return t;
  const legacy = sessionStorage.getItem(KEY);
  if (legacy) {
    localStorage.setItem(KEY, legacy);
    sessionStorage.removeItem(KEY);
  }
  return legacy;
};

export const setToken = (t: string) => localStorage.setItem(KEY, t);

// Both, always. A copy left in either half is a session that outlives the
// sign-out that was meant to end it.
export const clearToken = () => {
  localStorage.removeItem(KEY);
  sessionStorage.removeItem(KEY);
};

/**
 * End the session on the server as well as in this tab.
 *
 * `clearToken` only empties this tab's sessionStorage. The server also
 * accepts an `sdportal_session` cookie, which is twelve hours and survives a
 * closed tab — so a sign-out that forgot the cookie would leave a live
 * session behind, and the next page load would walk straight back in.
 *
 * Raw fetch rather than the client below: `call` reloads the page on a 401,
 * and signing out is the one moment a 401 is an expected answer.
 */
/**
 * The session lapsed and the SPA should return to its sign-in screen.
 *
 * An event rather than a reload: a reload cannot be made to happen before the
 * screens behind it render, and the flicker between the two was the whole
 * complaint.
 */
export const SESSION_EXPIRED = 'sdportal:session-expired';

export async function signOut(): Promise<void> {
  clearToken();
  try {
    await fetch('/api/auth/logout', { method: 'POST' });
  } catch {
    // The cookie outlives us by at most its twelve hours, and the tab is
    // already signed out. Not worth holding the UI for.
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
  });

  const body = (await res.json().catch(() => ({}))) as any;

  if (res.status === 401) {
    /*
     * The session lapsed. Sign out properly rather than reloading.
     *
     * This used to call location.reload(), which did not stop the function:
     * execution fell through to the throw below, every screen with a request
     * in flight rendered its error or empty state, and the reload landed on
     * top of whichever of those had painted. The fleet list showing
     * restaurants on one try and nothing on the next was that race, not a
     * data problem.
     *
     * The cookie goes too. It is the other half of the session and is twelve
     * hours long, so clearing only this tab's copy would leave the boot probe
     * free to pick the dead session back up.
     */
    void signOut();
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED));
  }
  if (!res.ok) {
    throw new ApiError(res.status, body.error ?? 'error', body.message ?? 'Request failed');
  }
  return body as T;
}

const get = <T>(p: string) => call<T>(p);
const post = <T>(p: string, b: unknown) =>
  call<T>(p, { method: 'POST', body: JSON.stringify(b) });
const patch = <T>(p: string, b: unknown) =>
  call<T>(p, { method: 'PATCH', body: JSON.stringify(b) });
// A DELETE may carry a body: removing a password needs the same
// re-confirmation that setting one does.
const del = <T>(p: string, b?: unknown) =>
  call<T>(p, { method: 'DELETE', ...(b === undefined ? {} : { body: JSON.stringify(b) }) });

// ---------------------------------------------------------------------------

export interface Identity {
  id: string;
  email: string;
  name: string;
  kind: 'scandeer' | 'reseller' | 'restaurant';
  role: 'admin' | 'member' | 'readonly';
  resellerId?: string;
}

/**
 * How long a health reading is worth believing. Mirrors HEALTH_FRESH_MS in the
 * worker's health.ts — if you change one, change the other.
 */
export const HEALTH_FRESH_MS = 45 * 60 * 1000;

/** The runner and the Worker disagree about a leading `v`. Neither is wrong. */
const bare = (v: string | null | undefined): string | null => (v ? v.replace(/^v/, '') : null);

export interface RunningVersion {
  /** What to show, and what to compare against the latest release. */
  version: string | null;
  /**
   * `reported` — the restaurant told us itself, recently. Believe this one.
   * `deployed` — nobody has asked it lately, so this is the deploy pipeline's
   *   claim, which is the thing that has been wrong before.
   */
  source: 'reported' | 'deployed' | 'none';
  /**
   * The restaurant is running something other than what the pipeline recorded.
   * Not an error for the operator to act on — it means our bookkeeping is
   * stale, and the next successful deploy re-stamps it.
   */
  drifted: boolean;
  /** The pipeline's claim, when it differs and is worth showing beside it. */
  recorded: string | null;
}

/**
 * Which version a restaurant is ACTUALLY on.
 *
 * `deployed_version` is written once by the deploy runner from what it
 * believed it shipped; `reported_version` comes from the restaurant's own
 * /health. When we have a fresh answer from the restaurant, that wins — it is
 * the only one of the two that comes from the running code.
 */
export function runningVersion(r: Restaurant, now: number = Date.now()): RunningVersion {
  const reported = bare(r.reported_version);
  const recorded = bare(r.deployed_version);
  const fresh = r.reported_at != null && now - r.reported_at < HEALTH_FRESH_MS;

  if (fresh && reported) {
    return {
      version: reported,
      source: 'reported',
      drifted: recorded != null && recorded !== reported,
      recorded,
    };
  }
  if (recorded) return { version: recorded, source: 'deployed', drifted: false, recorded };
  return { version: null, source: 'none', drifted: false, recorded: null };
}

export interface Restaurant {
  id: string;
  slug: string;
  name: string;
  status: 'draft' | 'connecting' | 'provisioning' | 'live' | 'suspended' | 'failed';
  reseller_id: string | null;
  timezone: string;
  contact_email: string | null;
  contact_phone: string | null;
  cf_account_id: string | null;
  worker_name: string | null;
  base_url: string | null;
  deployed_version: string | null;
  /** The viewer's position at this restaurant, decided by the server. */
  my_role?: RestaurantRole | null;
  reported_version: string | null;
  reported_at: number | null;
  migration_level: number;
  channel: string;
  health_state: 'unknown' | 'ok' | 'degraded' | 'down';
  health_checked_at: number | null;
  created_at: number;
  live_at: number | null;
  has_token: number;
  token_last4: string | null;
  token_verified_at: number | null;
  plan_name?: string;
  plan_price?: string;
  plan_status?: 'trial' | 'active' | 'expired';
  has_delivery?: boolean | number;
  /** 'whatsapp' when the printed table codes open WhatsApp; absent for the
   *  direct menu link, which is the default. */
  guest_qr?: string | null;
  trial_expires_at?: number;
  subscription_expires_at?: number | null;
  days_remaining?: number;
  /** The tokens stay on the server; this says only whether one is on file. */
  whatsapp_config?: {
    phoneNumberId?: string;
    wabaId?: string;
    hasAccessToken?: boolean;
    /**
     * The number as a guest's phone can dial it, digits only.
     *
     * Not a secret — it is printed on every table card. This, not
     * `phoneNumberId`, is what decides whether the restaurant has a usable
     * number of its own: the id is what the Cloud API sends WITH and cannot
     * open a chat.
     */
    displayNumber?: string;
    /** The number came through coexistence onboarding; the owner's app still works. */
    coexistence?: boolean;
    /** Meta confirmed the webhook subscription. False means guests get silence. */
    subscribed?: boolean;
    updatedAt?: number;
  } | null;
  /** Which delivery system this restaurant runs. NULL = never chosen. */
  delivery_provider?: 'none' | 'pos_whatsapp' | 'scandeer' | null;
  /** Redacted by the server — the key and the secret never reach a browser. */
  scandeer_config?: {
    baseUrl?: string;
    serviceId?: string;
    locationId?: string;
    hasApiKey?: boolean;
    hasWebhookSecret?: boolean;
    updatedAt?: number;
  } | null;
}

/** What the signed-in person is AT a restaurant. Not their portal role. */
export type RestaurantRole = 'owner' | 'admin' | 'cashier';

export interface RestaurantMember {
  user_id: string;
  email: string;
  name: string;
  role: RestaurantRole;
  avatar_url?: string | null;
  last_login_at?: number | null;
  has_google?: number;
  /** Digits, country code first. Set when they were added by, or linked, WhatsApp. */
  phone?: string | null;
  added_at?: number;
}

export interface Deployment {
  id: string;
  kind: string;
  state: string;
  runner_url: string | null;
  error: string | null;
  started_at: number;
  finished_at: number | null;
}

export interface Capability {
  id: string;
  label: string;
  ok: boolean;
  detail?: string;
}

export interface VerifyResult {
  ok: boolean;
  /** The account actually used — derived from the token when not supplied. */
  accountId?: string;
  accountName?: string;
  capabilities: Capability[];
  missing: string[];
  fatal?: string;
  /** Present when the token reaches several accounts and one must be picked. */
  chooseFrom?: { id: string; name: string }[];
}

export interface WhatsAppStart {
  code: string;
  /** Never shown or sent anywhere but back to /status. */
  pollSecret: string;
  /** wa.me link with "login billdedo <code>" already written. */
  waUrl: string;
  /** The number the message goes to, digits only. */
  number: string;
  expiresAt: number;
}

export type WhatsAppStatus =
  | { status: 'pending' }
  | { status: 'expired' }
  | { status: 'signed_in'; token: string; identity: Identity }
  | { status: 'linked'; phone: string }
  | { status: 'conflict'; phone: string; other: OtherAccountSummary }
  /** A re-confirmation, not a sign-in: proof, good for five minutes. */
  | { status: 'confirmed'; confirmToken: string }
  | { status: 'error'; error: string };

export interface SignInMethods {
  phone: string | null;
  /** Null when no Google account is linked; email null for a placeholder. */
  google: { email: string | null } | null;
  /** Whether a password is set. Never the hash, never a hint about it. */
  password: boolean;
  available: { whatsapp: boolean; google: boolean; password: boolean };
}

/** The account a phone or Google identity already belongs to, when linking it to this one. */
export interface OtherAccountSummary {
  name: string;
  /** Null when the account was created by WhatsApp and has no real email. */
  email: string | null;
  phone: string | null;
  hasGoogle: boolean;
  restaurantsOwned: number;
  /** Up to six, for display. */
  restaurantNames: string[];
  memberships: number;
  canMerge: boolean;
  /** False while it owns restaurants — deleting would orphan them. */
  canDelete: boolean;
}

export interface Checklist {
  /** Cloudflare's token form with the four permissions pre-selected. */
  createUrl: string;
  /** The bare token page, for when the pre-filled link misbehaves. */
  plainCreateUrl: string;
  steps: string[];
  permissions: { scope: string; name: string; level: string; why: string }[];
  note: string;
}

export const api = {
  /** Which sign-in methods the server offers. Safe to call unauthenticated. */
  authMethods: () =>
    get<{ password?: boolean; google: boolean; whatsapp?: boolean }>('/api/auth/methods'),

  /** Email or phone, plus a password the person set from inside a session. */
  login: (identifier: string, password: string) =>
    post<{ token: string; identity: Identity }>('/api/auth/login', { identifier, password }),

  /** Show a sign-in QR. Unauthenticated — this is the login screen. */
  whatsappStart: () => post<WhatsAppStart>('/api/auth/whatsapp/start', {}),

  /** Has the WhatsApp message arrived yet? Polled by the screen showing the QR. */
  whatsappStatus: (code: string, pollSecret: string) =>
    post<WhatsAppStatus>('/api/auth/whatsapp/status', { code, pollSecret }),

  /** Every way into the signed-in account, and which methods the portal offers. */
  signInMethods: () => get<SignInMethods>('/api/me/sign-in-methods'),

  /** Where to send the browser to link a Google account to this one. */
  linkGoogle: () => post<{ url: string }>('/api/me/google/link', {}),

  /** What a Google link collided with, from the token it came back with. */
  googleConflict: (token: string) =>
    post<
      | { status: 'conflict'; email: string; other: OtherAccountSummary }
      | { status: 'resolved'; email: string }
    >('/api/me/google/conflict', { token }),

  resolveGoogle: (token: string, action: 'merge' | 'delete') =>
    post<{ status: 'linked'; email: string; action: string }>('/api/me/google/resolve', {
      token,
      action,
    }),

  /** A QR that attaches the scanning phone to the account already signed in. */
  linkWhatsApp: () => post<WhatsAppStart>('/api/me/whatsapp/link', {}),

  /**
   * Re-confirm before setting a password. A session is not enough on its own
   * — see password.ts — so these two start the proof, and the token they
   * yield is what `setPassword` wants.
   */
  confirmPasswordWithGoogle: () => post<{ url: string }>('/api/me/password/confirm/google', {}),
  confirmPasswordWithWhatsApp: () => post<WhatsAppStart>('/api/me/password/confirm/whatsapp', {}),
  setPassword: (password: string, confirmToken: string) =>
    post<{ ok: true }>('/api/me/password', { password, confirmToken }),
  removePassword: (confirmToken: string) =>
    del<{ ok: true }>('/api/me/password', { confirmToken }),

  unlinkWhatsApp: () => del<{ ok: true }>('/api/me/whatsapp'),

  /** The phone had its own account: fold it into this one, or delete it. */
  resolveWhatsApp: (code: string, pollSecret: string, action: 'merge' | 'delete') =>
    post<{ status: 'linked'; phone: string; action: string }>('/api/me/whatsapp/resolve', {
      code,
      pollSecret,
      action,
    }),

  me: () => get<{ identity: Identity }>('/api/me'),

  restaurants: () => get<{ restaurants: Restaurant[]; latest_version?: string }>('/api/restaurants'),

  restaurant: (id: string) =>
    get<{ restaurant: Restaurant; latest_version?: string; deployments: Deployment[] }>(
      `/api/restaurants/${id}`,
    ),

  createRestaurant: (input: {
    name: string;
    slug?: string;
    timezone?: string;
    contactEmail?: string;
    contactPhone?: string;
    hasDelivery?: boolean | number;
  }) => post<{ id: string; slug: string }>('/api/restaurants', input),

  updateRestaurant: (id: string, input: Record<string, unknown>) =>
    patch<{ ok: true }>(`/api/restaurants/${id}`, input),

  checklist: () => get<Checklist>('/api/connect/checklist'),

  deploy: (id: string) =>
    post<{ jobId: string; kind: string; state: string }>(`/api/restaurants/${id}/deploy`, {}),

  /**
   * Ask this restaurant, right now, what it is running and how it is doing.
   *
   * The cron does this for the whole fleet every quarter hour; this is for
   * somebody standing in front of the page who has just deployed and does not
   * want to wait for it.
   */
  checkHealth: (id: string) =>
    post<{
      health_state: 'unknown' | 'ok' | 'degraded' | 'down';
      reported_version: string | null;
      schema_version?: number | null;
      checked_at?: number;
      checked: boolean;
      detail: string;
      drifted?: boolean;
    }>(`/api/restaurants/${id}/health`, {}),

  /**
   * Removes a restaurant that was never built — no Cloudflare token, no
   * account, never deployed. Scandeer admins only, and the server refuses
   * any restaurant with infrastructure behind it; both are enforced there.
   */
  deleteRestaurant: (id: string) => del<{ ok: true }>(`/api/restaurants/${id}`),

  /** Everyone on a restaurant, and whether the viewer may change the list. */
  members: (id: string) =>
    get<{ owner: RestaurantMember | null; members: RestaurantMember[]; canManage: boolean }>(
      `/api/restaurants/${id}/members`,
    ),

  /** By Google sign-in address, or by WhatsApp number (dial code and number apart). */
  addMember: (
    id: string,
    input:
      | { email: string; name?: string; role: 'admin' | 'cashier' }
      | { countryCode: string; phone: string; name?: string; role: 'admin' | 'cashier' },
  ) =>
    post<{ ok: true; user_id: string; email: string; phone: string | null; role: string }>(
      `/api/restaurants/${id}/members`,
      input,
    ),

  /** `sessionRevoked` is false when the till could not be told — see members.ts. */
  removeMember: (id: string, userId: string) =>
    del<{ ok: true; email: string; sessionRevoked: boolean; message: string }>(
      `/api/restaurants/${id}/members/${userId}`,
    ),

  /** Clears a restaurant's trade. Scandeer admins only; the server enforces it. */
  resetRestaurant: (id: string) =>
    post<{ ok: true; restaurant: string; cleared: Record<string, number> }>(
      `/api/restaurants/${id}/reset`,
      {},
    ),
  /** The catalogue, which `resetRestaurant` deliberately leaves alone. */
  resetRestaurantMenu: (id: string) =>
    post<{ ok: true; restaurant: string; cleared: Record<string, number> }>(
      `/api/restaurants/${id}/reset-menu`,
      {},
    ),

  createSession: (id: string) =>
    post<{
      ok: boolean;
      assertion: string;
      jti: string;
      expiresAt: number;
      baseUrl: string;
      restaurant: { id: string; name: string; slug: string };
    }>(`/api/restaurants/${id}/session`, {}),

  renewPlan: (id: string, planType: 'monthly' | 'annual' = 'monthly') =>
    post<{ ok: boolean; newExpiry: number; restaurant: Restaurant; message: string }>(
      `/api/restaurants/${id}/renew`,
      { planType },
    ),

  /**
   * Returns a 422 with the reasons when the token is wrong, so this resolves
   * rather than throws for the "not connected yet" case — the operator needs
   * to see WHICH permission is missing, not a generic failure.
   */
  /** `accountId` may be blank — the portal derives it from the token. */
  connect: async (id: string, accountId: string, token: string): Promise<VerifyResult> => {
    const res = await fetch(`/api/restaurants/${id}/connect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ accountId, token }),
    });

    const body = (await res.json().catch(() => ({}))) as any;

    // 422 is a *verified* answer, not a failure: the token reached Cloudflare
    // and came back missing permissions. VerifyResult carries which ones, and
    // the panel lists them — so it is returned, not thrown.
    if (res.status === 200 || res.status === 422) return body as VerifyResult;
    throw new ApiError(res.status, body.error ?? 'error', body.message ?? 'Could not connect');
  },

  users: () => get<{ users: PortalUser[] }>('/api/users'),
  updateUser: (id: string, b: { roleType?: 'super_admin' | 'admin' | 'user'; active?: boolean }) =>
    patch<{ ok: true }>(`/api/users/${id}`, b),

  getAdminSettings: () =>
    get<{ razorpay_key_id: string; razorpay_key_secret_set: boolean }>('/api/admin/settings'),

  saveAdminSettings: (data: { razorpay_key_id?: string; razorpay_key_secret?: string }) =>
    post<{ ok: boolean; message: string }>('/api/admin/settings', data),

  createPaymentOrder: (id: string, plan_type: 'monthly' | 'annual' = 'monthly') =>
    post<{
      ok: boolean;
      key_id: string;
      order_id: string;
      amount: number;
      currency: string;
      restaurant_name: string;
      plan_type: 'monthly' | 'annual';
    }>(`/api/restaurants/${id}/create-payment-order`, { plan_type }),

  verifyPayment: (
    id: string,
    payload: {
      razorpay_order_id: string;
      razorpay_payment_id: string;
      razorpay_signature: string;
      plan_type: 'monthly' | 'annual';
    },
  ) =>
    post<{ ok: boolean; newExpiry: number; restaurant: Restaurant; message: string }>(
      `/api/restaurants/${id}/verify-payment`,
      payload,
    ),

  /** The two Meta ids the coexistence onboarding runs under. */
  metaConfig: () => get<{ appId: string; configId: string }>('/api/meta-config'),

  connectWhatsApp: (
    id: string,
    input: {
      code?: string;
      wabaId?: string;
      phoneNumberId?: string;
      accessToken?: string;
      displayNumber?: string;
      coexistence?: boolean;
    },
  ) =>
    post<{ ok: boolean; message: string; whatsappConfig: any; connected: boolean }>(
      `/api/restaurants/${id}/connect-whatsapp`,
      input,
    ),

  /**
   * Connect — or, with deliveryProvider set to anything but 'scandeer',
   * disconnect — Scandeer delivery for one restaurant.
   *
   * Secrets left blank are kept: the server never sent them here to be
   * prefilled, so blank cannot mean "erase".
   */
  connectScandeer: (
    id: string,
    input: {
      baseUrl?: string;
      serviceId?: string;
      locationId?: string;
      apiKey?: string;
      webhookSecret?: string;
      deliveryProvider?: 'none' | 'pos_whatsapp' | 'scandeer';
    },
  ) =>
    post<{
      ok: boolean;
      message: string;
      scandeerConfig: any;
      deliveryProvider: string;
      pushed: boolean;
    }>(`/api/restaurants/${id}/connect-scandeer`, input),
};

export interface PortalUser {
  id: string;
  email: string;
  name: string;
  kind: 'scandeer' | 'reseller' | 'restaurant';
  role: 'admin' | 'member' | 'readonly';
  avatar_url?: string | null;
  active: number;
  created_at: number;
  last_login_at?: number | null;
}

export function timeAgo(at: number | null): string {
  if (!at) return 'never';
  const mins = Math.floor((Date.now() - at) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
